import useNeveForm from 'components/forms/NeveForm';
import strings from 'strings/strings.json';
import useUploadRequest from 'hooks/useUploadRequest';
import useModifyRequest from 'hooks/useModifyRequest';
import React from 'react';
import ResourcesList from '../../ResourcesList';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { CreateActivityFormProps } from './shared/CreateActivityFormProps';
import { Activity, ActivityType, BuildMethod, TaskContent } from 'models/Activity';
import { ActivityFormCard } from '../shared/ActivityFormCard';
import BaseDetailsForm from '../shared/DetailsForm';
import { HorizontalRule } from 'components/Elements';
import { CreateTextImageTaskRequest, TaskContentField } from 'models/inputModels/CreateTaskRequest';
import { FieldErrors, useFieldArray, UseFormRegister } from 'react-hook-form';
import { ModalState, ResourcesInput, TextImageTaskInput } from '../shared/SharedActivityFormComponents';
import { Modals, TextAndImages } from '../shared/SharedTextAndImageTaskForm';

export default function CreateTextImageTaskForm({
    returnToModulesList,
    moduleId,
}: CreateActivityFormProps): JSX.Element {
    const { refetchCourse, editMode, setCourseState } = React.useContext(BuildCourseContext);

    const { upload: uploadImage, uploading: uploadingImage } = useUploadRequest();
    const { modifyData: createTask, loading: savingTask } = useModifyRequest<CreateTextImageTaskRequest, boolean>(
        `activities/task/textimage`,
        'POST',
    );

    const [modalState, setModalState] = React.useState<ModalState>('closed');
    const [textRequiredError, setContentRequiredError] = React.useState(false);
    const [error, setError] = React.useState('');

    const addLinkButtonRef = React.useRef<HTMLButtonElement>(null);
    const addTextButtonRef = React.useRef<HTMLButtonElement>(null);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = useNeveForm<TextImageTaskInput>({
        title: '',
        description: '',
        durationInMins: null,
        resources: [],
        contents: [],
    });

    const {
        fields: resourceFields,
        append: appendResource,
        remove: removeResource,
    } = useFieldArray({
        control,
        name: 'resources',
    });
    const {
        fields: contentFields,
        append: appendContent,
        remove: removeContent,
    } = useFieldArray({
        control,
        name: 'contents',
    });

    const watchedContents = watch('contents');

    const addResource = (newResource: ResourcesInput) => {
        appendResource(newResource);
        addLinkButtonRef.current?.focus();
    };
    const deleteResource = (index: number) => {
        removeResource(index);
        addLinkButtonRef.current?.focus();
    };
    const closeResourcesModal = () => {
        setModalState('closed');
        addLinkButtonRef.current?.focus();
    };

    async function saveTask(formData: TextImageTaskInput) {
        if (!formData.contents.length) {
            setContentRequiredError(true);
            addTextButtonRef.current?.focus();
            return;
        }

        //MAP CONTENTS
        const contents: TaskContentField[] = [];
        for (const content of watchedContents) {
            switch (content.type) {
                case 'addText':
                    contents.push({
                        type: 'addText',
                        text: content.text,
                    });
                    break;
                case 'addImage':
                    const { errors, value } = await uploadImage(content.file);
                    if (errors?.length) {
                        setError('Error creating task');
                        return;
                    }
                    contents.push({
                        type: 'addImage',
                        fileName: value!.filename,
                        filePath: value!.s3Key,
                        imageDescription: content.imageDescription,
                    });
                    break;
            }
        }

        if (!editMode) {
            const request: CreateTextImageTaskRequest = {
                moduleId: moduleId,
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins ?? 0,
                resources: formData.resources,
                contents: contents,
            };
            const { errors } = await createTask(request);
            if (errors) {
                setError('Error creating task');
                return;
            }

            await refetchCourse();
            returnToModulesList();
            return;
        }

        setCourseState((prev) => {
            if (!prev) return;

            const moduleIndex = prev.modules.findIndex((m) => m.id === moduleId);
            if (moduleIndex === -1) return prev;
            const module = prev.modules[moduleIndex];

            const newActivityOrder =
                module.activities.length > 0 ? module.activities[module.activities.length - 1].order + 1 : 1;

            const taskContents: TaskContent[] = contents.map((content, index) => {
                const base = {
                    id: '', //new
                    order: index + 1,
                    audio: { s3Filepath: '' }, //no audio can be generated on edit
                };
                switch (content.type) {
                    case 'addText':
                        const addText: TaskContent = {
                            ...base,
                            type: 'text',
                            text: content.text,
                        };
                        return addText;
                    case 'addImage':
                        const addImage: TaskContent = {
                            ...base,
                            type: 'image',
                            imageDescription: content.imageDescription,
                            image: {
                                id: '', //new image
                                filename: content.fileName,
                                s3Filepath: content.filePath,
                            },
                        };
                        return addImage;
                    default:
                        throw new Error(`Unsupported content type`);
                }
            });

            const newActivity: Activity = {
                id: '',
                moduleId: moduleId,
                type: ActivityType.TASK,
                buildMethod: BuildMethod.TEXT_AND_IMAGE,
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins,
                order: newActivityOrder,
                resources: resourceFields,
                taskContents: taskContents,
                content: '',
                imageDocuments: [],
                dateCreated: new Date(),
                questions: [],
                statuses: [],
                meetings: [],
                videoDocument: null,
                audioDocument: null,
                generateContentStatus: null,
            };
            const updatedModules = [...prev.modules];
            updatedModules[moduleIndex] = {
                ...module,
                activities: [...module.activities, newActivity],
            };
            return { ...prev, modules: updatedModules };
        });
        returnToModulesList();
    }

    return (
        <>
            <ActivityFormCard
                title={strings.activityDetailsForm.instruction}
                activityType={ActivityType.TASK}
                onSubmit={handleSubmit(saveTask)}
                submitting={uploadingImage || savingTask}
                error={error}
                pageOne={
                    <DetailsAndLinks
                        register={register}
                        errors={errors}
                        resources={resourceFields}
                        deleteResource={deleteResource}
                        toggleModalOpen={() => setModalState((prev) => (prev === 'closed' ? 'addResource' : 'closed'))}
                        disabled={uploadingImage || savingTask}
                        addLinkButtonRef={addLinkButtonRef}
                    />
                }
                pageTwo={
                    <TextAndImages
                        fields={contentFields}
                        errors={errors}
                        register={register}
                        append={appendContent}
                        remove={removeContent}
                        addTextButtonRef={addTextButtonRef}
                        contentRequiredError={textRequiredError}
                        setContentRequiredError={setContentRequiredError}
                        setOpenImageModal={() => setModalState('addImage')}
                        disable={uploadingImage || savingTask}
                        watch={watch}
                    />
                }
            />
            <Modals
                modalState={modalState}
                closeResourceModal={closeResourcesModal}
                closeImageModal={() => setModalState('closed')}
                addResource={addResource}
                setImage={(imageFile: File, imageDescription: string) => {
                    appendContent({
                        type: 'addImage',
                        file: imageFile,
                        imageDescription: imageDescription,
                        previewUrl: URL.createObjectURL(imageFile),
                    });
                }}
            />
        </>
    );
}

type DetailsAndLinksProps = {
    register: UseFormRegister<TextImageTaskInput>;
    errors: FieldErrors<TextImageTaskInput>;
    resources: ResourcesInput[];
    deleteResource: (index: number) => void;
    toggleModalOpen: () => void;
    disabled: boolean;
    addLinkButtonRef: React.RefObject<HTMLButtonElement>;
};

function DetailsAndLinks({
    register,
    errors,
    resources,
    deleteResource,
    toggleModalOpen,
    disabled,
    addLinkButtonRef,
}: DetailsAndLinksProps) {
    return (
        <>
            <BaseDetailsForm register={register} errors={errors} autoFocus />
            <HorizontalRule />
            <ResourcesList
                resources={resources}
                deleteResource={deleteResource}
                toggleModalOpen={toggleModalOpen}
                disabled={disabled}
                addLinkButtonRef={addLinkButtonRef}
            />
        </>
    );
}
